/*
 * Firm Knowledge > Intake Templates as pure functions: the firm's saved
 * templates and the CaseBlink starters (visa starters and form intakes from
 * the catalog) as one row shape, grouped into Firm Templates, CaseBlink
 * Starters and Archived, with the counts the table shows. No React or IO.
 */
import {
  TEMPLATE_FIRST_NAME_TOKEN,
  type OutgoingIntakeFormNode,
  type OutgoingIntakeReminderSchedule,
  type OutgoingIntakeSavedTemplate,
  type OutgoingIntakeTemplate
} from "@/api/types/outgoingIntake";

import { intakeEstimate } from "../foreignNationalOutgoingIntakes/intakeEstimate";
import { stepsOf } from "../requestsIde/editor/stepModel";

export type IntakeTemplateRow = {
  /** A saved template's id, or the catalog id of a starter. */
  id: string;
  kind: "firm" | "starter";
  name: string;
  description: string | null;
  /** null reads as Any. */
  visa: string | null;
  /** A starter that is a government form's questions. */
  form: boolean;
  questions: number;
  /** null when the schema is not loaded (a starter). */
  sections: number | null;
  /** "12 questions · 12 min". */
  size: string;
  /** What every intake made from it starts with: "Note · Video", or null. */
  startsWith: string | null;
  sent: number;
  /** When an intake was last sent from it. */
  lastSentAt: string | null;
  archived: boolean;
  updatedAt: string | null;
  ownerId: string | null;
};

export type IntakeTemplateGroups = {
  firm: IntakeTemplateRow[];
  starters: IntakeTemplateRow[];
  archived: IntakeTemplateRow[];
};

export const asIso = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") {
    // A Firestore timestamp serialized as `{seconds}`.
    const { seconds } = value as { seconds?: unknown };
    if (typeof seconds === "number")
      return new Date(seconds * 1000).toISOString();
  }
  return null;
};

/** Questions (steps the client answers) and the sections they sit in. */
export const schemaCounts = (
  schema: OutgoingIntakeFormNode[]
): { questions: number; sections: number } => {
  const steps = stepsOf(schema);
  return {
    questions: steps.length,
    sections: new Set(steps.map((s) => s.sectionId).filter(Boolean)).size
  };
};

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/** "12 questions · 12 min" from a schema, or the catalog's count. */
export const sizeOf = (
  schema: OutgoingIntakeFormNode[] | null | undefined,
  fallbackQuestions = 0
): string => {
  if (!schema) return plural(fallbackQuestions, "question");
  const { questions, minutes } = intakeEstimate(schema);
  return [plural(questions, "question"), minutes ? `${minutes} min` : null]
    .filter(Boolean)
    .join(" · ");
};

/** "Note · Video", "Note", "Video", or null when it starts with nothing. */
export const startsWithLabel = (
  t: Pick<OutgoingIntakeSavedTemplate, "attorney_note" | "welcome_video_url">
): string | null =>
  [
    t.attorney_note?.trim() ? "Note" : null,
    t.welcome_video_url ? "Video" : null
  ]
    .filter(Boolean)
    .join(" · ") || null;

export const savedTemplateRow = (
  t: OutgoingIntakeSavedTemplate
): IntakeTemplateRow => {
  const counts = schemaCounts(t.schema ?? []);
  return {
    id: t.template_id,
    kind: "firm",
    name: t.name,
    description: t.description ?? null,
    visa: t.visa ?? null,
    form: false,
    questions: counts.questions,
    sections: counts.sections,
    size: sizeOf(t.schema ?? []),
    startsWith: startsWithLabel(t),
    sent: t.sent_count ?? 0,
    lastSentAt: asIso(t.last_sent_at),
    archived: Boolean(t.archived_at),
    updatedAt: asIso(t.updated_at) ?? asIso(t.created_at),
    ownerId: t.updated_by_user_id ?? t.created_by_user_id ?? null
  };
};

/** Catalog rows that are whole intakes: the visa starters and the forms. */
export const isStarter = (t: OutgoingIntakeTemplate): boolean =>
  t.category === "visa" || t.category === "form";

export const starterRow = (t: OutgoingIntakeTemplate): IntakeTemplateRow => ({
  id: t.template_id,
  kind: "starter",
  name: t.label,
  description:
    t.description ??
    (t.category === "form"
      ? `The questions ${t.agency ?? "USCIS"} ${t.label} needs.`
      : null),
  visa: t.visa ?? null,
  form: t.category === "form",
  questions: t.schema
    ? schemaCounts(t.schema).questions
    : (t.question_count ?? 0) + (t.document_count ?? 0),
  sections: t.schema ? schemaCounts(t.schema).sections : null,
  size: sizeOf(t.schema, (t.question_count ?? 0) + (t.document_count ?? 0)),
  startsWith: null,
  sent: 0,
  lastSentAt: null,
  archived: false,
  updatedAt: null,
  ownerId: null
});

const matches = (row: IntakeTemplateRow, query: string) => {
  const q = query.trim().toLocaleLowerCase();
  if (!q) return true;
  return [row.name, row.description ?? "", row.visa ?? ""]
    .join(" ")
    .toLocaleLowerCase()
    .includes(q);
};

const byUpdated = (a: IntakeTemplateRow, b: IntakeTemplateRow) =>
  (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "") ||
  a.name.localeCompare(b.name);

export const groupTemplates = (
  saved: OutgoingIntakeSavedTemplate[],
  catalog: OutgoingIntakeTemplate[],
  query = ""
): IntakeTemplateGroups => {
  const rows = saved.map(savedTemplateRow).filter((r) => matches(r, query));
  return {
    firm: rows.filter((r) => !r.archived).sort(byUpdated),
    starters: catalog
      .filter(isStarter)
      .map(starterRow)
      .filter((r) => matches(r, query))
      .sort(
        (a, b) =>
          Number(a.form) - Number(b.form) || a.name.localeCompare(b.name)
      ),
    archived: rows.filter((r) => r.archived).sort(byUpdated)
  };
};

/** "Not sent yet", "Sent once", "Sent 14 times". */
export const sentLabel = (n: number): string =>
  n === 0 ? "Not sent yet" : n === 1 ? "Sent once" : `Sent ${n} times`;

/** The name a duplicate gets: a starter becomes an Intake, a copy says so. */
export const duplicateName = (row: Pick<IntakeTemplateRow, "name" | "kind">) =>
  row.kind === "starter"
    ? `${row.name.replace(/\s*Starter$/i, "").trim()} Intake`
    : `${row.name} (Copy)`;

/**
 * A template stores reminders "off" as an empty schedule, because null means
 * it sets none (new intakes get the standard cadence).
 */
export const TEMPLATE_REMINDERS_OFF: OutgoingIntakeReminderSchedule = {
  days_after_send: [],
  days_before_expiry: null
};

const isOff = (s: OutgoingIntakeReminderSchedule) =>
  !s.days_after_send.length && !s.days_before_expiry;

/** The template's reminders as the cadence picker reads them (null is Off). */
export const templateReminders = (
  stored: OutgoingIntakeReminderSchedule | null | undefined,
  standard: OutgoingIntakeReminderSchedule
): OutgoingIntakeReminderSchedule | null =>
  !stored ? standard : isOff(stored) ? null : stored;

/** What the template stores for the picker's choice. */
export const storedReminders = (
  chosen: OutgoingIntakeReminderSchedule | null
): OutgoingIntakeReminderSchedule => chosen ?? TEMPLATE_REMINDERS_OFF;

/** A template's note as a client reads it. */
export const fillNote = (note: string, firstName: string): string =>
  note.split(TEMPLATE_FIRST_NAME_TOKEN).join(firstName || "there");
